import { ApiClient } from '../apiClient';
import {
  AddToCartDto,
  UpdateCartItemDto,
  RemoveFromCartDto,
  UpdateCartDetailsDto,
  AddToCartResponse,
  UpdateCartItemResponse,
  RemoveFromCartResponse,
  GetCartResponse,
  EmptyCartResponse,
  UpdateCartDetailsResponse,
  MergeCartRequest,
  MergeCartResponse,
  EmailCheckResponse
} from '../../types/cartApi';
import { ValidationError } from '../errors';
import { getPosthogDistinctId } from '../../lib/posthog-utils';
import { getUserEmail } from '../../utils/getUserEmail';
import { getFbp, getFbc } from '../../components/MetaPixel/MetaPixel';

export class CartService {
  constructor(private apiClient: ApiClient) {}

  /**
   * Add product to cart or create new cart
   */
  async addToCart(addToCartDto: AddToCartDto): Promise<AddToCartResponse> {
    // Validate required fields
    if (!addToCartDto.productId || addToCartDto.productId.trim() === '') {
      throw new ValidationError('Product ID is required');
    }

    if (!addToCartDto.quantity || addToCartDto.quantity <= 0) {
      throw new ValidationError('Quantity must be greater than 0');
    }

    if (addToCartDto.selectedValue === undefined || addToCartDto.selectedValue < 0) {
      throw new ValidationError('Selected value must be greater than or equal to 0');
    }

    // Add user email from localStorage if available
    const userEmail = getUserEmail();
    const posthogDistinctId = getPosthogDistinctId();
    const fbp = addToCartDto.fbp ?? getFbp() ?? undefined;
    const fbc = addToCartDto.fbc ?? getFbc() ?? undefined;
    const requestPayload = {
      ...addToCartDto,
      ...(userEmail && { email: userEmail }),
      ...(posthogDistinctId && { posthogDistinctId }),
      ...(fbp && { fbp }),
      ...(fbc && { fbc }),
    };

    return this.apiClient.post<AddToCartResponse>('/cart/add', requestPayload);
  }

  /**
   * Update cart item
   */
  async updateCartItem(updateCartDto: UpdateCartItemDto): Promise<UpdateCartItemResponse> {
    // Validate required fields
    if (!updateCartDto.cartId || updateCartDto.cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    if (!updateCartDto.productId || updateCartDto.productId.trim() === '') {
      throw new ValidationError('Product ID is required');
    }

    // Validate optional fields if provided
    if (updateCartDto.quantity !== undefined && updateCartDto.quantity <= 0) {
      throw new ValidationError('Quantity must be greater than 0');
    }

    if (updateCartDto.selectedValue !== undefined && updateCartDto.selectedValue < 0) {
      throw new ValidationError('Selected value must be greater than or equal to 0');
    }

    // Add user email from localStorage if available
    const userEmail = getUserEmail();
    const requestPayload = {
      ...updateCartDto,
      ...(userEmail && { email: userEmail })
    };

    return this.apiClient.put<UpdateCartItemResponse>('/cart/update', requestPayload);
  }

  /**
   * Remove product from cart
   */
  async removeFromCart(removeFromCartDto: RemoveFromCartDto): Promise<RemoveFromCartResponse> {
    // Validate required fields
    if (!removeFromCartDto.cartId || removeFromCartDto.cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    if (removeFromCartDto.productIndex == undefined || removeFromCartDto.productIndex < 0) {
      throw new ValidationError('Product Index is required');
    }

    // Add user email from localStorage if available
    const userEmail = getUserEmail();
    const requestPayload = {
      ...removeFromCartDto,
      ...(userEmail && { email: userEmail })
    };

    return this.apiClient.deleteWithBody<RemoveFromCartResponse>('/cart/remove', requestPayload);
  }

  /**
   * Get cart by ID
   */
  async getCartById(cartId: string): Promise<GetCartResponse> {
    if (!cartId || cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    return this.apiClient.get<GetCartResponse>(`/cart/${cartId}`);
  }

  /**
   * Empty cart (keep cart, remove all products)
   */
  async emptyCart(cartId: string): Promise<EmptyCartResponse> {
    if (!cartId || cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    return this.apiClient.delete<EmptyCartResponse>(`/cart/${cartId}/empty`);
  }

  /**
   * Update cart details (email)
   */
  async updateCartDetails(updateCartDetailsDto: UpdateCartDetailsDto): Promise<UpdateCartDetailsResponse> {
    // Validate required fields
    if (!updateCartDetailsDto.cartId || updateCartDetailsDto.cartId.trim() === '') {
      throw new ValidationError('Cart ID is required');
    }

    return this.apiClient.put<UpdateCartDetailsResponse>(`/cart/${updateCartDetailsDto.cartId}/details`, updateCartDetailsDto);
  }

  /**
   * Ask whether a checkout address can receive mail and whether it looks
   * like a typo of a known provider.
   */
  async checkEmail(email: string): Promise<EmailCheckResponse> {
    return this.apiClient.post<EmailCheckResponse>('/cart/email-check', { email });
  }

  /**
   * Merge the browser's guest cart + all pending server-side carts for the
   * authenticated user into a single cart. Auth is by cookie (withCredentials).
   */
  async mergeCart(guestCartId?: string): Promise<MergeCartResponse> {
    const payload: MergeCartRequest = {};
    if (guestCartId && guestCartId.trim() !== '') {
      payload.guestCartId = guestCartId;
    }
    return this.apiClient.post<MergeCartResponse>('/cart/merge', payload);
  }
}
